import { Attachment, Section } from 'api/types'
import { Button, IconButton } from 'components/ui'
import { t } from 'lib/i18n'

import { AttachmentActions, AttachmentChips, UploadButton } from './Attachments'

export const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`

export interface Column<T> {
  key: keyof T & string
  label: string
  type?: 'text' | 'number' | 'textarea' | 'select'
  list?: string
  options?: { value: string; label: string }[]
  emptyOptionLabel?: string
  placeholder?: string
  size?: 'sm' | 'md' | 'lg'
}

interface Props<T extends { id?: string }> {
  lines: T[]
  columns: Column<T>[]
  onChange: (lines: T[]) => void
  makeLine: () => T
  addLabel: string
  readOnly: boolean
  emptyText: string
  section?: Section
  attachments?: Attachment[]
  attachmentActions?: AttachmentActions
  attachmentRequired?: boolean
  uploadingLine?: string | null
  onPreview?: (attachment: Attachment) => void
  onUploadLine?: (line: T, files: File[]) => void
}

export const LinesEditor = <T extends { id?: string }>({
  lines,
  columns,
  onChange,
  makeLine,
  addLabel,
  readOnly,
  emptyText,
  attachments,
  attachmentActions,
  attachmentRequired,
  uploadingLine,
  onPreview,
  onUploadLine,
}: Props<T>) => {
  const withFiles = Boolean(attachmentActions && onUploadLine)

  const update = (index: number, key: keyof T, value: string) => {
    const next = lines.slice()
    next[index] = { ...next[index], [key]: value }
    onChange(next)
  }

  const remove = (index: number) => onChange(lines.filter((_, position) => position !== index))

  return (
    <div className="lines">
      {lines.length ? (
        <table className="lines__table">
          <thead>
            <tr>
              <th scope="col" className="lines__index">
                #
              </th>
              {columns.map((column) => (
                <th key={column.key} scope="col" className={column.size ? `col--${column.size}` : undefined}>
                  {column.label}
                </th>
              ))}
              {withFiles ? (
                <th scope="col">
                  {t('المرفقات', 'Attachments')}
                  {attachmentRequired ? ' *' : ''}
                </th>
              ) : null}
              {!readOnly ? (
                <th scope="col">
                  <span className="sr-only">{t('حذف', 'Delete')}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const files = attachments?.filter((attachment) => line.id && attachment.lineId === line.id) ?? []
              const missing = attachmentRequired && !files.length
              return (
                <tr key={line.id ?? index} className={missing ? 'is-missing-file' : undefined}>
                  <td className="lines__index num">{index + 1}</td>
                  {columns.map((column) => {
                    const value = (line[column.key] as unknown as string | number | null | undefined) ?? ''
                    const label = t(`${column.label} - البند ${index + 1}`, `${column.label} - Line ${index + 1}`)
                    const optionLabel = column.options?.find((option) => option.value === String(value))?.label
                    return (
                      <td key={column.key} data-label={column.label}>
                        {readOnly ? (
                          <span className="lines__value">{(column.type === 'select' ? optionLabel : String(value)) || '—'}</span>
                        ) : column.type === 'textarea' ? (
                          <textarea
                            className="input input--area"
                            rows={2}
                            aria-label={label}
                            placeholder={column.placeholder}
                            value={value}
                            onChange={(event) => update(index, column.key, event.target.value)}
                          />
                        ) : column.type === 'select' ? (
                          <select
                            className="input"
                            aria-label={label}
                            value={value}
                            onChange={(event) => update(index, column.key, event.target.value)}
                          >
                            <option value="">{column.emptyOptionLabel ?? '—'}</option>
                            {column.options?.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            className="input"
                            type={column.type === 'number' ? 'number' : 'text'}
                            inputMode={column.type === 'number' ? 'numeric' : undefined}
                            min={column.type === 'number' ? 0 : undefined}
                            list={column.list}
                            aria-label={label}
                            placeholder={column.placeholder}
                            value={value}
                            onChange={(event) => update(index, column.key, event.target.value)}
                          />
                        )}
                      </td>
                    )
                  })}
                  {withFiles && attachmentActions && onUploadLine ? (
                    <td data-label={t('المرفقات', 'Attachments')}>
                      <div className="lines__files">
                        <AttachmentChips attachments={files} actions={attachmentActions} onPreview={onPreview ?? (() => undefined)} />
                        {!readOnly ? (
                          <UploadButton
                            label={files.length ? t('إضافة', 'Add') : t('إرفاق', 'Attach')}
                            busy={uploadingLine === line.id}
                            required={missing}
                            onFiles={(selected) => onUploadLine(line, selected)}
                          />
                        ) : !files.length ? (
                          <span className="muted">—</span>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                  {!readOnly ? (
                    <td className="lines__remove">
                      <IconButton icon="trash" label={t(`حذف البند ${index + 1}`, `Delete line ${index + 1}`)} onClick={() => remove(index)} />
                    </td>
                  ) : null}
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : (
        <p className="lines__empty">{emptyText}</p>
      )}
      {!readOnly ? (
        <Button size="sm" variant="ghost" icon="plus" onClick={() => onChange([...lines, { ...makeLine(), id: newId() }])}>
          {addLabel}
        </Button>
      ) : null}
    </div>
  )
}
